import React, { useCallback, useMemo, useState } from 'react';

import { Box, Typography } from '@mui/material';

import { useNumberState } from '@client/hooks/use-number-state.hooks';
import { useProject, useProjectBuildingGFA } from '@client/store/project';
import { useApplyProjectChanges } from '@client/store/project/use-apply-project-changes.hook';
import type { ProjectChange } from '@shared/helpers/pipeline/update-engine.models';
import { Icon } from '@ui/Icon';
import { SidePanel } from '@ui/layout';
import { PropertySliderSection } from '@ui/PropertySliderSection';
import { Slider, useSliderState } from '@ui/Slider';
import { UnitNumberInput } from '@ui/UnitNumberInput';
import { getDisplayValueData } from '@shared/helpers/display/get-display-value-data.helper';
import { getDisplayNumber } from '@shared/helpers/display/get-display-number.helper';
import { isProductCategory } from '@shared/helpers/element-category.helpers';
import { getProduct } from '@shared/helpers/product.helpers';
import { getCombinedConversionFactors } from '@shared/helpers/results.helpers';
import {
  getEPDProductId,
  getProductElementData,
  isGenericProductElement,
} from '@shared/helpers/product-element.helpers';
import { getSelectableUnitsInConversionFactors } from '@shared/helpers/unit.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import { type ProductCategoryId } from '@shared/models/element-categories.model';
import { ElementPropertyName } from '@shared/models/element-property.model';
import {
  createProductElementCarbonLevelOverrideChange,
  createProductElementTransportOverrideChange,
  createProductElementWasteOverrideChange,
} from '@shared/helpers/pipeline/product-element-override-changes.helpers';
import {
  type IElement,
  type IProductElement,
  type ProductCategoryElement,
} from '@shared/models/project.model';
import { type ProductID } from '@shared/models/product.model';
import { useDisplayCurrency } from '@features/currency/selected-display-currency.hook';
import { useSelectedVersionProductsAndStoreProducts } from '@client/store/ui';
import { useVersionExpressionVariablesRecord } from '@client/hooks/use-element-expression-variables.hooks';
import { reusedContentProductId } from '@shared/templates/categories/reused-content-property.model';
import amplitudeLog from '@client/amplitude';
import {
  AMP_PRODUCT_UPDATE_CARBON_SLIDER,
  AMP_PRODUCT_UPDATE_LIFETIME,
  AMP_PRODUCT_UPDATE_REUSE,
  AMP_PRODUCT_UPDATE_SPILL,
  AMP_PRODUCT_UPDATE_TRANSPORT,
} from '@shared/constants';

import {
  defaultPerUnit,
  getBulkEditConversionFactorDisplayValue,
  getBulkPropertyValuesFromPropertyElements,
  getBulkPropertyValuesFromProductElements,
  getCo2eAdjustmentValues,
  getEPDPropertyDelta,
  hasCarbonAdjustmentOverride,
  getPerUnitDivideBy,
  getPerUnitLabel,
  getPropertyValuesFromProducts,
  getWeightedFactorFromPercentile,
  getWeightedPercentileFactorRange,
  setPreferredPerUnit,
  useElementPropertyCommitHandler,
  usePreferredPerUnit,
} from './product-bulk-edit-form.helpers';
import { BulkEditEpdSelector } from './components/BulkEditEpdSelector';
import { ValueRow } from './components/ValueRow';
import { getProductClimateImpactLevel } from '@shared/helpers/products/product-climate-impact-level.helpers';
import {
  createMarketEstimatesSnapshot,
  factorToPercentile,
} from '@shared/helpers/product-market-estimates.helpers';
import {
  getProductConservativeFactor,
  NEUTRAL_CONSERVATIVE_FACTOR,
} from '@shared/helpers/conservative-carbon.helpers';
import { useConservativeGenericCarbon } from '@features/results/conservative-generic-carbon.hook';
import {
  SelectPerUnit,
  type PerUnitOptionValue,
} from '../components/SelectPerUnit';
import { EllipsisText } from '@ui/EllipsisText';

export type ProductBulkEditFormProps = {
  /** Product category element (Wood or Metal etc.). Used to determine EPD filtering when selecting EPDs. */
  productCategoryElement?: ProductCategoryElement;

  /**
   * Elements that enable element-based inputs.
   * Omit or pass an empty array to hide those inputs and related sections.
   */
  elements?: IElement[];

  /** Product elements that drive product-related inputs and overrides. */
  productElements: IProductElement[];
  onEpdSelect?: (id: ProductID, genericId?: ProductID) => void;
  disableEpdSelector?: boolean;
  showGenericProductName?: boolean;
};

/**
 * Stable string key for the current element and product-element scope.
 * Use as a dependency or comparison value to reset local state when the
 * selection changes (e.g. close edit mode or reset slider to aggregate).
 *
 * @param elements - {@link IElement}s in scope
 * @param productElements - {@link IProductElement}s in scope
 * @returns Key that changes when either array's identity or contents change
 */
const useScopeKey = (
  elements: IElement[],
  productElements: IProductElement[],
): string =>
  useMemo(() => {
    return [
      ...elements.map((element) => element.id),
      ...productElements.map(
        ({ product_id, generic_product_id }) =>
          `${product_id}/${generic_product_id}`,
      ),
    ].join('|');
  }, [elements, productElements]);

/**
 * Input props for a mixed-value number input.
 * Use {@link placeholder} to show mixed state, {@link valueProp} for the
 * override value, and {@link fallbackProp} when a consistent base exists.
 */
type MixedInputValueProps = {
  /** 'mixed' when values differ; otherwise omitted. */
  placeholder: 'mixed' | undefined;
  /** Override value to display; omitted when mixed or no override exists. */
  valueProp: number | undefined;
  /** Base fallback value to display when not mixed. */
  fallbackProp: number | undefined;
};

/**
 * Inputs for deriving {@link MixedInputValueProps}.
 */
type MixedInputValueConfig = {
  /** True when selected values differ. */
  mixed: boolean;
  /** True when at least one explicit value exists. */
  hasExplicitValue: boolean;
  /** The resolved override value to show when not mixed. */
  value: number;
  /** Base value used as fallback when values are consistent. */
  fallbackValue?: number;
  /** Optional mapper for display (e.g. rounding or unit conversion). */
  mapValue?: (value: number) => number;
};

/**
 * Derive mixed-input display props from element data.
 * Returns undefined display fields when inputs are mixed or missing.
 */
const getMixedInputValueProps = ({
  mixed,
  hasExplicitValue,
  value,
  fallbackValue,
  mapValue = (inputValue) => inputValue,
}: MixedInputValueConfig): MixedInputValueProps => {
  const placeholder = mixed ? 'mixed' : undefined;
  const valueProp = mixed || !hasExplicitValue ? undefined : mapValue(value);
  const fallbackProp =
    !mixed && guards.is.number(fallbackValue)
      ? mapValue(fallbackValue)
      : undefined;
  return { placeholder, valueProp, fallbackProp };
};

/** Slider operates in percentile range (can change to 0-100 later) */
const SLIDER_PERCENTILE_RANGE = { min: 5, max: 50 };

export const ProductBulkEditForm: React.FC<ProductBulkEditFormProps> = ({
  productCategoryElement,
  elements = [],
  productElements,
  onEpdSelect,
  disableEpdSelector = false,
  showGenericProductName = false,
}) => {
  const productsLookup = useSelectedVersionProductsAndStoreProducts();
  const variablesRecord = useVersionExpressionVariablesRecord();
  const gfa = useProjectBuildingGFA();
  const applyChanges = useApplyProjectChanges();
  const { marketEstimates: projectMarketEstimates } = useProject();
  // Use project's estimates if available, otherwise create fresh snapshot. Project will receive the snapshot when saved always.
  const marketEstimates = useMemo(
    () => projectMarketEstimates ?? createMarketEstimatesSnapshot(),
    [projectMarketEstimates],
  );
  const { current: currentDisplayCurrency } = useDisplayCurrency();
  const productsInScope = useMemo(() => {
    return productElements
      .map(
        (pElement) => productsLookup[getProductElementData(pElement).genericId],
      )
      .filter(guards.is.defined);
  }, [productsLookup, productElements]);
  const productCategory = useMemo<ProductCategoryId | undefined>(() => {
    const categories = productsInScope
      .map(({ category_id }) => category_id)
      .filter(isProductCategory);
    const uniqueCategories = new Set(categories);
    return uniqueCategories.size === 1 ? categories[0] : undefined;
  }, [productsInScope]);

  const showReusedContentSection = useMemo(() => {
    // hide it when the only product element in selection is reused content itself
    if (productElements.length === 0) return false;
    const genericIds = new Set(
      productElements.map(
        (productElement) => getProductElementData(productElement).genericId,
      ),
    );
    return !(genericIds.size === 1 && genericIds.has(reusedContentProductId));
  }, [productElements]);

  const preferredPerUnit = usePreferredPerUnit();
  const selectablePerUnits = useMemo(() => {
    const defaultUnits: PerUnitOptionValue[] = ['total', 'GFA'];
    if (productsInScope.length === 0) return defaultUnits;
    const [firstProduct, ...restProducts] = productsInScope;
    const firstUnits = getSelectableUnitsInConversionFactors(
      getCombinedConversionFactors(undefined, firstProduct),
    );
    const restUnitSets = restProducts.map(
      (product) =>
        new Set(
          getSelectableUnitsInConversionFactors(
            getCombinedConversionFactors(undefined, product),
          ),
        ),
    );
    const commonUnits = firstUnits.filter((unit) =>
      restUnitSets.every((set) => set.has(unit)),
    );
    return [...defaultUnits, ...commonUnits];
  }, [productsInScope]);

  const selectedPerUnit = useMemo(() => {
    if (preferredPerUnit && selectablePerUnits.includes(preferredPerUnit)) {
      return preferredPerUnit;
    }
    return selectablePerUnits[0] ?? defaultPerUnit;
  }, [preferredPerUnit, selectablePerUnits]);

  const perUnitOptions = useMemo(
    () =>
      selectablePerUnits.map((unit) => ({
        value: unit,
        label: getPerUnitLabel(unit),
      })),
    [selectablePerUnits],
  );

  const handlePerUnitChange = useCallback((unit: PerUnitOptionValue) => {
    setPreferredPerUnit(unit);
  }, []);

  const productData = useMemo(
    () => ({
      wastePercent: getBulkPropertyValuesFromProductElements({
        productsMap: productsLookup,
        productElements,
        property: 'co2e_waste_percent',
        marketEstimates,
      }),
      transport: getBulkPropertyValuesFromProductElements({
        productsMap: productsLookup,
        productElements,
        property: 'co2e_transport',
        marketEstimates,
      }),
      co2e_A1A3: getPropertyValuesFromProducts(
        productsLookup,
        productElements,
        'co2e_A1-A3',
        selectedPerUnit,
        gfa,
        marketEstimates,
      ),
      sek_A1A3: getPropertyValuesFromProducts(
        productsLookup,
        productElements,
        'sek_A1-A3',
        selectedPerUnit,
        gfa,
        marketEstimates,
      ),
    }),
    [gfa, marketEstimates, productElements, selectedPerUnit, productsLookup],
  );

  // Weighted percentile factor range for EPD mode slider positioning
  const weightedPercentileFactorRange = useMemo(
    () =>
      getWeightedPercentileFactorRange(
        productData.co2e_A1A3.fromItems,
        productsLookup,
        marketEstimates,
      ),
    [productData.co2e_A1A3.fromItems, productsLookup, marketEstimates],
  );
  const productNameData = useMemo(() => {
    const names = new Set(
      productElements
        .map(
          (element) =>
            getProduct(
              productsLookup,
              getProductElementData(element).currentId,
              false,
            )?.name,
        )
        .filter(guards.is.defined),
    );
    const mixed = names.size > 1;
    return {
      mixed,
      name: mixed ? 'mixed' : Array.from(names).at(0) || 'Unknown',
    };
  }, [productElements, productsLookup]);

  const genericProductNameDisplay = useMemo(() => {
    if (!showGenericProductName || productsInScope.length === 0) return null;
    const names = new Set(
      productsInScope.map((p) => p.name).filter(guards.is.defined),
    );
    const mixed = names.size > 1;
    return {
      mixed,
      name: mixed ? 'mixed' : (Array.from(names).at(0) ?? 'Unknown'),
    };
  }, [showGenericProductName, productsInScope]);

  const isEpdSelectorDisabled = disableEpdSelector || productNameData.mixed;

  const hasSingleEpdForAll = useMemo(() => {
    const epdIds = productElements
      .map((element) => getEPDProductId(element))
      .filter(guards.is.defined);
    if (epdIds.length !== productElements.length) return false;
    return new Set(epdIds).size === 1;
  }, [productElements]);
  const normalizedEpdDeltas = useMemo(() => {
    if (!hasSingleEpdForAll) return undefined;
    const co2eDelta = getEPDPropertyDelta({
      productsMap: productsLookup,
      productElements,
      property: 'co2e_A1-A3',
      perUnit: selectedPerUnit,
      gfa,
    });
    const costDelta = getEPDPropertyDelta({
      productsMap: productsLookup,
      productElements,
      property: 'sek_A1-A3',
      perUnit: selectedPerUnit,
      gfa,
    });
    return {
      co2ePercent: co2eDelta.value,
      costPercent: costDelta.value,
    };
  }, [
    gfa,
    hasSingleEpdForAll,
    productElements,
    selectedPerUnit,
    productsLookup,
  ]);

  const sliderPercentileLimits = useMemo(
    () => ({
      carbon: SLIDER_PERCENTILE_RANGE,
      cost: SLIDER_PERCENTILE_RANGE,
    }),
    [],
  );

  const co2eAdjustmentData = useMemo(
    () => getCo2eAdjustmentValues(productElements),
    [productElements],
  );

  const conservativeGenericCarbon = useConservativeGenericCarbon();
  const hasCarbonOverride = useMemo(
    () => hasCarbonAdjustmentOverride(productElements),
    [productElements],
  );
  // The pipeline only uplifts generic (non-EPD) elements, and the slider sets a single
  // percentile for the whole selection — so the conservative fallback may only apply when every
  // selected element is generic. Mixed or EPD-backed selections keep their measured position
  // (DEV-3411).
  const isFullyGenericSelection = useMemo(
    () =>
      productElements.length > 0 &&
      productElements.every(isGenericProductElement),
    [productElements],
  );
  // Average per-product conservative factor across the (all-generic) selection; missing factor
  // defaults to the generic 1.25 uplift (see getProductConservativeFactor, DEV-3411).
  const averageConservativeFactor = useMemo(() => {
    if (productsInScope.length === 0) return NEUTRAL_CONSERVATIVE_FACTOR;
    const sum = productsInScope.reduce(
      (acc, product) => acc + getProductConservativeFactor(product),
      0,
    );
    return sum / productsInScope.length;
  }, [productsInScope]);
  // In conservative mode, an all-generic selection with no explicit override falls back to the
  // slider percentile representing the products' conservative uplift (DEV-3411).
  const conservativeFallbackPercentile =
    conservativeGenericCarbon &&
    !hasCarbonOverride &&
    isFullyGenericSelection &&
    averageConservativeFactor > NEUTRAL_CONSERVATIVE_FACTOR &&
    weightedPercentileFactorRange
      ? factorToPercentile(
          averageConservativeFactor - 1,
          weightedPercentileFactorRange,
        )
      : undefined;

  const [co2ePercentile, setCo2ePercentile] = useNumberState(
    conservativeFallbackPercentile ?? co2eAdjustmentData.average,
    {
      clamp: [
        sliderPercentileLimits.carbon.min,
        sliderPercentileLimits.carbon.max,
      ],
    },
  );
  const isEpdDerivedActive = hasSingleEpdForAll;

  // In EPD mode: convert actual EPD percentage to where it sits in market distribution
  // In estimate mode: use the slider's percentile directly
  const effectiveCo2ePercentile =
    hasSingleEpdForAll && weightedPercentileFactorRange
      ? factorToPercentile(
          (normalizedEpdDeltas?.co2ePercent ?? 0) / 100,
          weightedPercentileFactorRange,
        )
      : co2ePercentile;

  // Displayed percentage value
  // In EPD mode: use actual calculated EPD delta (no re-conversion needed)
  // In estimate mode: convert slider percentile to weighted average factor
  const { co2ePercentAdjustment, costPercentAdjustment } = useMemo(() => {
    if (hasSingleEpdForAll && normalizedEpdDeltas) {
      return {
        co2ePercentAdjustment: normalizedEpdDeltas.co2ePercent,
        costPercentAdjustment: normalizedEpdDeltas.costPercent,
      };
    }
    const { carbonFactor, costFactor } = getWeightedFactorFromPercentile(
      productData.co2e_A1A3.fromItems,
      co2ePercentile,
      productsLookup,
      marketEstimates,
    );
    return {
      co2ePercentAdjustment: carbonFactor * 100,
      costPercentAdjustment: costFactor * 100,
    };
  }, [
    hasSingleEpdForAll,
    normalizedEpdDeltas,
    productData.co2e_A1A3.fromItems,
    co2ePercentile,
    productsLookup,
    marketEstimates,
  ]);

  const co2eFactorAdjustment = co2ePercentAdjustment / 100;
  const costAdjustment = {
    percent: costPercentAdjustment,
    factor: costPercentAdjustment / 100,
    multiplier: 1 + costPercentAdjustment / 100,
  };

  const reuseData = useMemo(
    () =>
      getBulkPropertyValuesFromPropertyElements({
        elements,
        propertyName: ElementPropertyName.ReusedContent,
        variablesRecord,
      }),
    [elements, variablesRecord],
  );
  const lifetimeData = useMemo(
    () =>
      getBulkPropertyValuesFromPropertyElements({
        elements,
        propertyName: ElementPropertyName.Lifetime,
        variablesRecord,
      }),
    [elements, variablesRecord],
  );
  const commitReuseElements = useElementPropertyCommitHandler(
    reuseData.fromItems,
    ElementPropertyName.ReusedContent,
    variablesRecord,
  );
  const commitReuseElementsWithTracking = useCallback(
    (value?: number) => {
      commitReuseElements(value);
      amplitudeLog(AMP_PRODUCT_UPDATE_REUSE, { Value: value });
    },
    [commitReuseElements],
  );
  const reuseScopeKey = useScopeKey(elements, productElements);
  const co2ePercentDisplay = Math.round(co2ePercentAdjustment);
  const costPercentDisplay = Math.round(costAdjustment.percent);
  const commitLifetimeElements = useElementPropertyCommitHandler(
    lifetimeData.fromItems,
    ElementPropertyName.Lifetime,
    variablesRecord,
  );
  const commitLifetimeElementsWithTracking = useCallback(
    (value?: number) => {
      commitLifetimeElements(value);
      amplitudeLog(AMP_PRODUCT_UPDATE_LIFETIME, { Value: value });
    },
    [commitLifetimeElements],
  );
  const lifetimeSlider = useSliderState({
    value: lifetimeData.current.average,
    onCommit: commitLifetimeElementsWithTracking,
  });

  const [isAdjustmentSliderDragging, setIsAdjustmentSliderDragging] =
    useState(false);
  const showAdjustmentMixed =
    !hasSingleEpdForAll &&
    co2eAdjustmentData.mixed &&
    !isAdjustmentSliderDragging;

  const isCo2eLabelFallback = !showAdjustmentMixed && co2ePercentDisplay === 0;
  const isCostLabelFallback = !showAdjustmentMixed && costPercentDisplay === 0;

  const perUnitDivideBy = useMemo(
    () => getPerUnitDivideBy(selectedPerUnit),
    [selectedPerUnit],
  );

  const co2eDisplay = getDisplayValueData(
    {
      value: getBulkEditConversionFactorDisplayValue(productData.co2e_A1A3, {
        useEpdFactors: hasSingleEpdForAll,
        adjustmentMultiplier: 1 + co2eFactorAdjustment,
      }),
      unit: 'co2e_A1-A3',
    },
    { divideBy: perUnitDivideBy, outputUnit: 'kgCO2e' },
  );

  const displayCurrency =
    currentDisplayCurrency === 'NONE' ? 'SEK' : currentDisplayCurrency;

  const costDisplay = getDisplayValueData(
    {
      value: getBulkEditConversionFactorDisplayValue(productData.sek_A1A3, {
        useEpdFactors: hasSingleEpdForAll,
        adjustmentMultiplier: costAdjustment.multiplier,
      }),
      unit: 'sek_A1-A3',
    },
    { divideBy: perUnitDivideBy, outputUnit: displayCurrency },
  );

  const [wastePercentValue, setWastePercentValue] = useNumberState(
    productData.wastePercent.current.average,
  );

  const wastePercentFallbackValue = productData.wastePercent.hasFallbackValue
    ? productData.wastePercent.fallback.average
    : undefined;

  const [transportValue, setTransportValue] = useNumberState(
    productData.transport.current.average,
  );
  const transportFallbackValue = productData.transport.hasFallbackValue
    ? productData.transport.fallback.average
    : undefined;

  const wastePercentInputProps = getMixedInputValueProps({
    mixed: productData.wastePercent.current.mixed,
    hasExplicitValue: productData.wastePercent.hasExplicitValue,
    value: wastePercentValue,
    fallbackValue: wastePercentFallbackValue,
    mapValue: (value) => Math.round(value) / 100,
  });
  const transportInputProps = getMixedInputValueProps({
    mixed: productData.transport.current.mixed,
    hasExplicitValue: productData.transport.hasExplicitValue,
    value: transportValue,
    fallbackValue: transportFallbackValue,
  });

  const applyPercentileOverride = useCallback(
    (percentileValue: number) => {
      if (!Number.isFinite(percentileValue)) {
        console.error(
          'applyPercentileOverride: received non-finite percentile value',
          percentileValue,
        );
        return;
      }
      const { fromItems } = productData.co2e_A1A3;

      const changes: ProjectChange[] = fromItems.map((element) =>
        createProductElementCarbonLevelOverrideChange(
          element.id,
          percentileValue === 0 ? undefined : percentileValue,
        ),
      );

      void applyChanges(changes);
    },
    [applyChanges, productData.co2e_A1A3],
  );

  const applyWastePercentOverride = useCallback(
    (value: number) => {
      const changes: ProjectChange[] = productData.wastePercent.fromItems.map(
        (element) => createProductElementWasteOverrideChange(element.id, value),
      );
      void applyChanges(changes);
    },
    [applyChanges, productData.wastePercent.fromItems],
  );

  const applyTransportOverride = useCallback(
    (value: number) => {
      const changes: ProjectChange[] = productData.transport.fromItems.map(
        (element) =>
          createProductElementTransportOverrideChange(element.id, value),
      );
      void applyChanges(changes);
    },
    [applyChanges, productData.transport.fromItems],
  );

  // Slider handlers for CO2e and cost (cost is inverted: 100 - co2ePercentile)
  const co2ePercentileHandlers = useMemo(
    () => ({
      onChange: (_e: unknown, v: number, _thumb: number) =>
        setCo2ePercentile(v),
      onCommit: (_e: unknown, v: number) => applyPercentileOverride(v),
    }),
    [applyPercentileOverride, setCo2ePercentile],
  );

  const handleWastePercentInputChange = useCallback(
    (value?: number) => {
      amplitudeLog(AMP_PRODUCT_UPDATE_SPILL, { Value: value });
      if (guards.is.undefined(value)) {
        void applyChanges(
          productData.wastePercent.fromItems.map((element) =>
            createProductElementWasteOverrideChange(element.id, undefined),
          ),
        );
        return;
      }
      const percentValue = Math.round(value * 100);
      setWastePercentValue(percentValue);
      applyWastePercentOverride(percentValue);
    },
    [
      applyWastePercentOverride,
      productData.wastePercent.fromItems,
      setWastePercentValue,
      applyChanges,
    ],
  );
  const handleTransportInputChange = useCallback(
    (value?: number) => {
      amplitudeLog(AMP_PRODUCT_UPDATE_TRANSPORT, { Value: value });
      if (!guards.is.number(value)) {
        void applyChanges(
          productData.transport.fromItems.map((element) =>
            createProductElementTransportOverrideChange(element.id, undefined),
          ),
        );
        return;
      }
      setTransportValue(value);
      applyTransportOverride(value);
    },
    [
      applyTransportOverride,
      productData.transport.fromItems,
      setTransportValue,
      applyChanges,
    ],
  );
  return (
    <>
      {elements.length > 0 && showReusedContentSection && (
        <>
          <PropertySliderSection
            label="Reuse"
            data={reuseData}
            onCommit={commitReuseElementsWithTracking}
            resetKey={reuseScopeKey}
          />
          <SidePanel.Content.Divider />
        </>
      )}
      {elements.length > 0 && (
        <>
          <SidePanel.Content.Section>
            <ValueRow columns="1fr 1fr">
              <Box
                sx={{
                  display: 'inline-flex',
                  alignSelf: 'center',
                  justifyContent: 'start',
                }}
              >
                Lifetime
              </Box>
              <UnitNumberInput
                unit="years"
                size="small"
                outlineVisibility="hover"
                unitAlignment="none"
                placeholder={lifetimeData.mixed ? 'mixed' : undefined}
                fallback={
                  !lifetimeData.mixed && lifetimeData.hasFallbackValue
                    ? lifetimeData.fallback.average
                    : undefined
                }
                value={(() => {
                  if (lifetimeSlider.isDragging) return lifetimeSlider.value;
                  if (!lifetimeData.hasExplicitValue) return undefined;
                  if (lifetimeData.current.mixed) return undefined;
                  return lifetimeData.hasExplicitValue
                    ? lifetimeData.current.average
                    : undefined;
                })()}
                min={0}
                onChange={commitLifetimeElementsWithTracking}
              />
            </ValueRow>
            <Slider
              min={0}
              max={100}
              value={lifetimeSlider.value}
              onChange={lifetimeSlider.onChange}
              onChangeCommitted={lifetimeSlider.onChangeCommitted}
            />
          </SidePanel.Content.Section>
          <SidePanel.Content.Divider />
        </>
      )}
      <SidePanel.Content.Section>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {genericProductNameDisplay && (
            <EllipsisText color="text.subtle">
              <Typography
                variant="body2"
                sx={{
                  display: 'inline',
                }}
              >
                {genericProductNameDisplay.mixed
                  ? 'mixed'
                  : genericProductNameDisplay.name}
              </Typography>
            </EllipsisText>
          )}
          {productData.co2e_A1A3.hasCompatibleItems && (
            <>
              <ValueRow columns="auto 1fr auto" gap={2}>
                <BulkEditEpdSelector
                  disabled={isEpdSelectorDisabled}
                  productElements={productData.co2e_A1A3.fromItems}
                  productCategoryElement={productCategoryElement}
                  onSelect={onEpdSelect}
                />
                {/* some stupid layout hack here. want the sides auto-sized but also need ellipsis in the button */}
                <div />
                <SelectPerUnit
                  value={selectedPerUnit}
                  options={perUnitOptions}
                  onChange={handlePerUnitChange}
                />
              </ValueRow>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    alignItems: 'center',
                    py: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'nowrap',
                      gap: 1,
                      alignItems: 'baseline',
                    }}
                  >
                    Carbon{' '}
                    <Typography
                      sx={{
                        fontWeight: 'bold',
                      }}
                    >
                      {getProductClimateImpactLevel(
                        productCategory,
                        co2ePercentDisplay,
                      )}
                    </Typography>
                    {showAdjustmentMixed ? (
                      <Typography
                        sx={{
                          color: 'text.subtle',
                        }}
                      >
                        (mixed)
                      </Typography>
                    ) : (
                      <Typography
                        color={
                          isCo2eLabelFallback
                            ? 'text.secondary'
                            : 'text.primary'
                        }
                        sx={{
                          fontWeight: isCo2eLabelFallback ? undefined : 'bold',
                          opacity: co2ePercentDisplay !== 0 ? 1 : 0,
                          transition: 'opacity 0.2s ease-out',
                        }}
                      >
                        (
                        {`${co2ePercentDisplay > 0 ? '+' : ''}${
                          getDisplayValueData({
                            value: co2ePercentDisplay / 100,
                            unit: '%',
                          }).full
                        }`}
                        )
                      </Typography>
                    )}
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'nowrap',
                      gap: 1,
                      alignItems: 'center',
                    }}
                  >
                    <Icon category="costEmission" icon="kgCO2e" />{' '}
                    {co2eDisplay.value}
                  </Box>
                </Box>
                <Slider
                  min={sliderPercentileLimits.carbon.min}
                  max={sliderPercentileLimits.carbon.max}
                  value={effectiveCo2ePercentile}
                  trackOrigin="end"
                  onChange={(e, v, t) => {
                    setIsAdjustmentSliderDragging(true);
                    co2ePercentileHandlers.onChange(e, v, t);
                  }}
                  onChangeCommitted={(e, v) => {
                    co2ePercentileHandlers.onCommit(e, v);
                    amplitudeLog(AMP_PRODUCT_UPDATE_CARBON_SLIDER, {
                      Value: v,
                    });
                    setIsAdjustmentSliderDragging(false);
                  }}
                  disabled={isEpdDerivedActive}
                />
                {currentDisplayCurrency !== 'NONE' && (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr auto',
                      alignItems: 'center',
                      py: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'nowrap',
                        gap: 1,
                        alignItems: 'baseline',
                      }}
                    >
                      Expenses
                      {showAdjustmentMixed ? (
                        <Typography
                          sx={{
                            color: 'text.subtle',
                          }}
                        >
                          (mixed)
                        </Typography>
                      ) : (
                        <Typography
                          color={
                            isCostLabelFallback
                              ? 'text.secondary'
                              : 'text.primary'
                          }
                          sx={{
                            opacity: costPercentDisplay !== 0 ? 1 : 0,
                            transition: 'opacity 0.2s ease-out',
                          }}
                        >
                          (
                          {`${costPercentDisplay > 0 ? '+' : ''}${
                            getDisplayValueData({
                              value: costPercentDisplay / 100,
                              unit: '%',
                            }).full
                          }`}
                          )
                        </Typography>
                      )}
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'nowrap',
                        gap: 1,
                        alignItems: 'center',
                      }}
                    >
                      <Icon category="currency" icon={displayCurrency}></Icon>
                      {getDisplayNumber(costDisplay.valueAsNumber, {
                        notation: 'compact',
                      })}
                    </Box>
                  </Box>
                )}
              </Box>
            </>
          )}

          {productData.transport.hasCompatibleItems && (
            <ValueRow columns="1fr 1fr">
              <Typography>Transport</Typography>
              <UnitNumberInput
                unit="km"
                size="small"
                outlineVisibility="hover"
                unitAlignment="none"
                placeholder={transportInputProps.placeholder}
                value={transportInputProps.valueProp}
                fallback={transportInputProps.fallbackProp}
                min={0}
                onChange={handleTransportInputChange}
              />
            </ValueRow>
          )}
          {productData.wastePercent.hasCompatibleItems && (
            <ValueRow columns="1fr 1fr">
              <Typography>Spill</Typography>
              <UnitNumberInput
                unit="%"
                size="small"
                outlineVisibility="hover"
                unitAlignment="none"
                placeholder={wastePercentInputProps.placeholder}
                value={wastePercentInputProps.valueProp}
                fallback={wastePercentInputProps.fallbackProp}
                min={0}
                max={1}
                onChange={handleWastePercentInputChange}
              />
            </ValueRow>
          )}
        </Box>
      </SidePanel.Content.Section>
    </>
  );
};
